import { dataLayerConfig, TAGButton, TAGSvgIcon, useAppContext, useFacilityContext } from '@aspendental/shared-components-web';
import { getSessionId } from '@aspendental/shared-utils/utils';
import { Box, Button, Card, CardContent, Grid, Link, Stack, SvgIcon, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { REASON_FOR_VISIT_ID } from '../ReasonForVisit/ReasonForVisit.helpers';
import { addToCalendar } from './Confirmation.helpers';
import { linkStyles } from './Confirmation.styles';
import MarketingCard from './MarketingCard/MarketingCard';
import PhoneNumberLink from '@src/components/PhoneNumberLink';
import { DataLayerStepNames, FALLBACK_FACILITY_PHONE_NUMBER, PATIENT_SERVICES_BASE_URL } from '@src/constants';
import { useCopyToClipboard } from '@src/hooks/useCopyToClipboard';
import { useLanguagePreferenceTracking } from '@src/hooks/useLanguagePreferenceTracking';
import iconMap from '@src/icons';
import { Card as MultiCardCollection, SuccessCard } from '@src/lib/__generated/contentful-sdk';
import { useAppointmentStore, useAppStore, usePatientInformationFormStore, useReasonForVisitStore } from '@src/store';
import { fireDataLayerEvent } from '@src/utils/data-layer-utils';
import { getBaseUrl } from '@src/utils/get-base-url';
import { sessionStorageHelper } from '@src/utils/session-storage-utils';
import { trimTimeZoneOffset } from '@src/utils/trimTimeZoneOffset';
export default function Confirmation({
  successCardsCollection,
  marketingContent
}: {
  successCardsCollection: {
    items: Array<SuccessCard>;
  };
  marketingContent: {
    cardsCollection: {
      items: Array<MultiCardCollection>;
    };
  };
}) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    mottoFlow
  } = useAppStore(store => store);
  const [, copyToClipboard] = useCopyToClipboard();
  const [isCopied, setIsCopied] = useState(false);
  const CalendarDateTimeIcon = iconMap.CalendarDateTime ?? (() => null);
  const {
    appointmentSlot,
    appointmentFacility,
    appointmentNumber,
    previsitToken
  } = useAppointmentStore(store => store);
  const {
    accountNumber,
    hasInsurance
  } = usePatientInformationFormStore(store => store);
  const {
    reasonForVisit
  } = useReasonForVisitStore(store => store);
  const webFacilityDetails = useFacilityContext();
  const {
    t
  } = useTranslation('confirmation');
  const router = useRouter();
  const {
    locale
  } = router;
  const {
    environment
  } = useAppContext();
  const baseUrl = getBaseUrl(environment);
  const location = `${appointmentFacility?.address?.line1} ${appointmentFacility?.address?.city}, ${appointmentFacility?.address?.state}  ${appointmentFacility?.zipCode}`;

  // setting the session storage key to false for the patient information step to prevent multiple data layer events
  sessionStorageHelper.setKey(DataLayerStepNames.confirmation, 'false');
  const openPrevisitPaperwork = () => {
    dataLayerConfig({
      auth_method: 'none',
      user_action: 'click',
      environment: environment,
      link_text: 'Fill out forms',
      tag_session_id: getSessionId(),
      event: 'new_patient_forms_start',
      page_origin_name: 'Confirmation',
      facility_id: appointmentFacility?.facilityCode,
      page_origin_url: `${baseUrl}/schedule-an-appointment/`,
      link_url: `${baseUrl}/${PATIENT_SERVICES_BASE_URL}/authentication/?token={token}&redirectTo=forms&channel=end_of_scheduling`
    });
    window.open(`/${PATIENT_SERVICES_BASE_URL}/authentication?token=${previsitToken}&redirectTo=forms&channel=end_of_scheduling`, '_blank', 'noopener,noreferrer');
  };
  useEffect(() => {
    //this method is added to prevent multiple data layer events from being fired in mobile view
    //pass the step name as first parameter
    fireDataLayerEvent(DataLayerStepNames.confirmation, {
      language_preference: locale,
      brand: mottoFlow ? 'mottoaligners' : 'aspendental',
      lead_score: appointmentSlot?.patientValue,
      lead_tier_national: appointmentSlot?.nationalPatientTier,
      lead_tier_office: appointmentSlot?.patientTier,
      step_name: DataLayerStepNames.confirmation,
      reason_for_visit: reasonForVisit,
      reason_for_visit_id: REASON_FOR_VISIT_ID[reasonForVisit],
      dental_insurance: String(!!hasInsurance),
      ...(webFacilityDetails?.officeInfo?.code && {
        website_selected_facility_id: webFacilityDetails?.officeInfo?.code
      }),
      ...(webFacilityDetails?.officeInfo?.name && {
        website_selected_facility_name: webFacilityDetails?.officeInfo?.name
      }),
      ...(appointmentFacility?.facilityCode && {
        appointment_facility_id: appointmentFacility?.facilityCode
      }),
      ...(appointmentFacility?.title && {
        appointment_facility_name: appointmentFacility?.title
      }),
      account_id: accountNumber,
      appointment_id: appointmentNumber
    });
  }, [reasonForVisit, webFacilityDetails?.officeInfo?.code, webFacilityDetails?.officeInfo?.name, appointmentFacility?.facilityCode, appointmentFacility?.title, hasInsurance, accountNumber, appointmentNumber, appointmentSlot, mottoFlow]);
  Object.keys(DataLayerStepNames).forEach(key => {
    sessionStorageHelper.removeKey(DataLayerStepNames[key]);
  });
  useLanguagePreferenceTracking({
    stepName: DataLayerStepNames.confirmation,
    webFacilityDetails,
    brand: mottoFlow ? 'mottoaligners' : 'aspendental'
  });
  return <>
			<Grid container spacing={2} direction={{
      xs: 'column-reverse',
      sm: 'row'
    }} data-sentry-element="Grid" data-sentry-source-file="Confirmation.tsx">
				<Grid item xs={12} lg={6} data-sentry-element="Grid" data-sentry-source-file="Confirmation.tsx">
					<Card elevation={0} variant="outlined" sx={{
          bgcolor: 'backgrounds.neutral'
        }} data-sentry-element="Card" data-sentry-source-file="Confirmation.tsx">
						<CardContent data-sentry-element="CardContent" data-sentry-source-file="Confirmation.tsx">
							<Grid container direction={{
              xs: 'column',
              sm: 'row'
            }} data-sentry-element="Grid" data-sentry-source-file="Confirmation.tsx">
								<Grid item xs={12} sm={2} alignSelf={{
                xs: 'center',
                sm: 'start'
              }} data-sentry-element="Grid" data-sentry-source-file="Confirmation.tsx">
									<TAGSvgIcon icon="AspenOffice" data-sentry-element="TAGSvgIcon" data-sentry-source-file="Confirmation.tsx" />
								</Grid>
								<Grid item xs={12} sm={10} data-sentry-element="Grid" data-sentry-source-file="Confirmation.tsx">
									<Stack alignItems={{
                  xs: 'center',
                  sm: 'flex-start'
                }} data-sentry-element="Stack" data-sentry-source-file="Confirmation.tsx">
										<Typography variant={isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook'} color="text.primary" data-private data-sentry-element="Typography" data-sentry-source-file="Confirmation.tsx">
											{appointmentFacility?.address?.line1}
										</Typography>
										<Typography variant={isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook'} color="text.primay" data-private data-sentry-element="Typography" data-sentry-source-file="Confirmation.tsx">{`${appointmentFacility?.address?.city}, ${appointmentFacility?.address?.state}  ${appointmentFacility?.zipCode}`}</Typography>
										<Box data-private data-sentry-element="Box" data-sentry-source-file="Confirmation.tsx">
											<PhoneNumberLink phoneNumber={appointmentFacility?.phone ?? FALLBACK_FACILITY_PHONE_NUMBER} variant={isSmallScreen ? 'bodyMediumBold' : 'bodyLargeBold'} data-private data-sentry-element="PhoneNumberLink" data-sentry-source-file="Confirmation.tsx" />
										</Box>
									</Stack>
								</Grid>
							</Grid>
							<Grid container direction={{
              xs: 'column',
              sm: 'row'
            }} mt={2} data-sentry-element="Grid" data-sentry-source-file="Confirmation.tsx">
								<Grid item xs={12} sm={2} alignSelf={{
                xs: 'center',
                sm: 'start'
              }} data-sentry-element="Grid" data-sentry-source-file="Confirmation.tsx">
									<SvgIcon viewBox="0 0 32 32" sx={{
                  height: 32,
                  width: 32
                }} data-sentry-element="SvgIcon" data-sentry-source-file="Confirmation.tsx">
										<CalendarDateTimeIcon data-sentry-element="CalendarDateTimeIcon" data-sentry-source-file="Confirmation.tsx" />
									</SvgIcon>
								</Grid>
								<Grid item xs={12} sm={10} data-sentry-element="Grid" data-sentry-source-file="Confirmation.tsx">
									<Stack alignItems={{
                  xs: 'center',
                  sm: 'flex-start'
                }} data-sentry-element="Stack" data-sentry-source-file="Confirmation.tsx">
										<Typography variant={isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook'} color="text.primary" data-private data-sentry-element="Typography" data-sentry-source-file="Confirmation.tsx">
											{new Intl.DateTimeFormat(locale, {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }).format(new Date(trimTimeZoneOffset(appointmentSlot?.time) ?? ''))}
										</Typography>
										<Typography variant={isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook'} color="text.primary" data-private data-sentry-element="Typography" data-sentry-source-file="Confirmation.tsx">
											{new Intl.DateTimeFormat(locale, {
                      hour: '2-digit',
                      minute: '2-digit'
                    }).format(new Date(trimTimeZoneOffset(appointmentSlot?.time) ?? ''))}
										</Typography>
										<Link component="button" type="button" data-test-id="add-to-calendar-button" variant={isSmallScreen ? 'bodyMediumBold' : 'bodyLargeBold'} color="secondary.main" title="addToCalendar" sx={linkStyles} onClick={() => addToCalendar(appointmentSlot?.time ?? '', reasonForVisit, location)} data-sentry-element="Link" data-sentry-source-file="Confirmation.tsx">
											{t('addToCalendar')}
										</Link>
									</Stack>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
				{previsitToken ? <Grid item xs={12} lg={6}>
						<Card elevation={0} variant="outlined" sx={{
          bgcolor: 'backgrounds.neutral',
          height: '100%'
        }}>
							<CardContent>
								<Grid container direction={{
              xs: 'row',
              sm: 'row'
            }}>
									<Grid item xs={2} sm={2} alignSelf={{
                xs: 'start',
                sm: 'start'
              }}>
										<TAGSvgIcon icon="Clipboard" />
									</Grid>
									<Grid item xs={10} sm={10}>
										<Stack alignItems={{
                  xs: 'flex-start',
                  sm: 'flex-start'
                }} gap="1.25rem">
											<Typography variant="header4" color="text.primary" data-test-id="previsit-title">
												Make your visit a breeze
											</Typography>
											<Typography color="text.primary" variant="bodyMediumBook" data-test-id="previsit-subtitle">
												Fill out your paperwork online before your appointment
											</Typography>
											<TAGButton size="M" component="a" type="submit" target="_blank" variant="primaryDefault" rel="noopener noreferrer" data-test-id="previsit-button" onClick={openPrevisitPaperwork}>
												Fill out forms
											</TAGButton>
										</Stack>
									</Grid>
								</Grid>
							</CardContent>
						</Card>
					</Grid> : <Grid item xs={12} lg={7}>
						<Card elevation={0} variant="outlined" sx={{
          bgcolor: 'backgrounds.neutral',
          height: '100%'
        }}>
							<CardContent>
								<Grid container direction={{
              xs: 'column',
              sm: 'row'
            }} alignItems={{
              xs: 'center',
              sm: 'start'
            }}>
									<Grid item xs={12} sm={5}>
										<Stack direction={{
                  xs: 'column'
                }} alignItems={{
                  xs: 'center',
                  sm: 'flex-start'
                }}>
											<Typography variant="overline" color="text.primary" sx={{
                    lineHeight: '1.75rem'
                  }}>
												{t('acctNumber1')}
											</Typography>
											<Typography variant="header4" color="text.primary" data-private>
												{accountNumber}
											</Typography>
										</Stack>
									</Grid>
									<Grid item>
										<Tooltip title={<span aria-live="assertive">
													<Box sx={visuallyHidden}>{t('acctNumber2')} </Box>
													{t('copied')}
												</span>} open={isCopied} disableFocusListener disableHoverListener disableTouchListener onClose={() => null} describeChild>
											<Button data-test-id="copy-account-number-button" size="M" variant="secondaryDefault" onClick={() => {
                    copyToClipboard(accountNumber);
                    setIsCopied(true);
                    setTimeout(() => {
                      setIsCopied(false);
                    }, 1500);
                  }} sx={{
                    mt: 0.75
                  }}>
												{t('copy')}&nbsp;
												<Box sx={visuallyHidden}>{t('acctNumber3')}</Box> {t('toClip')}
											</Button>
										</Tooltip>
									</Grid>
									<Grid item xs={12} mt={2}>
										<Typography variant={isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook'} color="text.primary">
											{t('takeNote')}
										</Typography>
									</Grid>
								</Grid>
							</CardContent>
						</Card>
					</Grid>}
			</Grid>
			<Grid container spacing={2} mt={2} data-sentry-element="Grid" data-sentry-source-file="Confirmation.tsx">
				{marketingContent?.cardsCollection?.items?.length > 0 ? marketingContent.cardsCollection.items.map(card => {
        if (card) {
          return <MarketingCard card={card} key={card?.sys?.id} />;
        }
      }) : successCardsCollection?.items.map(card => {
        if (card) {
          return <MarketingCard card={card} key={card?.sys.id} />;
        }
      })}
			</Grid>
		</>;
}